// AI Training page data
export const aiTrainingData = {
  // Hero section
  hero: {
    title: "AI Training & Education Programs",
    subtitle: "Empower your team with practical AI skills through our specialized training programs",
    imageUrl: "/assets/images/services/ai-training-hero.jpg"
  },
  
  // Page metadata
  title: "AI Training & Education Programs",
  subtitle: "Empower your team with practical AI skills through our specialized training programs",
  description: "Comprehensive training programs designed to upskill your team on the latest AI technologies and best practices.",
  overview: "Our AI Training & Education programs provide comprehensive, hands-on learning experiences tailored to your team's existing skill levels and your organization's specific goals. Whether you need to build foundational AI literacy across your organization or develop specialized technical capabilities in key roles, our training solutions combine theoretical knowledge with practical application to ensure your team can effectively leverage AI technologies.",
  serviceName: "AI Training & Education",
  nextService: {
    name: "Business AI Onboarding",
    url: "/services/ai-onboarding"
  },
  relatedCaseStudies: ["case-study-1", "case-study-3"],
  
  // Key benefits section
  keyBenefits: [
    {
      title: "Increased Productivity",
      description: "Automate repetitive tasks and streamline workflows, allowing your team to focus on higher-value activities.",
      icon: "productivity"
    },
    {
      title: "Custom Learning Paths",
      description: "Training programs tailored to individual roles and experience levels for maximum relevance.",
      icon: "path"
    },
    {
      title: "Real-World Applications",
      description: "Practical, hands-on exercises based on real business scenarios in your industry.",
      icon: "application"
    },
    {
      title: "Reduced Implementation Time",
      description: "Faster adoption of AI tools and methodologies with properly trained staff.",
      icon: "time"
    },
    {
      title: "Competitive Advantage",
      description: "Gain an edge in your market by developing AI capabilities ahead of industry peers.",
      icon: "advantage"
    },
    {
      title: "Continuous Support",
      description: "Ongoing access to AI experts for questions and guidance post-training.",
      icon: "support"
    }
  ],
  
  // FAQs
  faqs: [
    {
      question: "What types of AI training do you offer?",
      answer: "We offer a range of training programs, from introductory AI literacy courses for all employees to specialized technical training for data scientists and developers. Our programs include AI Fundamentals Workshops, Role-Based Skills Programs, Implementation Certification, and Custom AI Solutions Development training."
    },
    {
      question: "How long does training typically take?",
      answer: "Training duration varies based on the program and your team's existing knowledge. Our workshops can be completed in a single day, while more comprehensive programs may span several weeks. We work with you to create a schedule that minimizes disruption to your operations."
    },
    {
      question: "Can you customize training for our specific industry?",
      answer: "Absolutely. All our training programs are tailored to your industry, business processes, and specific use cases. We incorporate relevant examples, exercises, and projects that directly relate to the challenges and opportunities in your sector."
    },
    {
      question: "Do we need technical expertise before starting training?",
      answer: "No prior technical expertise is required for our foundational programs. We design our training to match your team's current skill levels, whether they're complete beginners or experienced technologists looking to expand their AI capabilities."
    },
    {
      question: "How do you measure training effectiveness?",
      answer: "We establish clear learning objectives at the outset and use a combination of assessments, project outcomes, and post-training surveys to measure effectiveness. For many clients, we also help establish KPIs to track how the training impacts actual business performance."
    }
  ],
  
  // Training programs section
  programs: [
    {
      title: "AI Fundamentals Workshop",
      description: "A one-day intensive workshop introducing key AI concepts, applications, and implementation considerations for decision-makers and leadership teams.",
      features: [
        "Overview of AI technologies and capabilities",
        "Business use case identification",
        "Implementation strategy fundamentals",
        "ROI calculation frameworks",
        "Risk assessment and management"
      ],
      forWhom: "Executives, Directors, Managers",
      duration: "1 day",
      format: "In-person or virtual, interactive workshop"
    },
    {
      title: "Role-Based AI Skills Program",
      description: "Targeted training series focusing on AI applications and skills specific to different business functions and professional roles.",
      features: [
        "Function-specific AI applications",
        "Hands-on tool training",
        "Process optimization tactics",
        "Integration with existing workflows",
        "Practical implementation exercises"
      ],
      forWhom: "Department teams, Specialists",
      duration: "3-5 days",
      format: "Modular training with hands-on labs"
    },
    {
      title: "AI Implementation Certification",
      description: "Comprehensive program covering the entire AI implementation lifecycle, from planning to deployment and optimization.",
      features: [
        "Project management methodology",
        "Technical integration planning",
        "Change management strategies",
        "Testing and validation methods",
        "Performance monitoring and improvement"
      ],
      forWhom: "IT Teams, Project Managers, Implementation Leads",
      duration: "2-4 weeks",
      format: "Blended learning with capstone project"
    },
    {
      title: "Custom AI Solutions Development",
      description: "Advanced training for technical teams on developing and customizing AI solutions for specific business requirements.",
      features: [
        "AI framework fundamentals",
        "Model selection and tuning",
        "Data preparation and management",
        "Solution architecture design",
        "Deployment and scaling strategies"
      ],
      forWhom: "Developers, Data Scientists, Engineers",
      duration: "4-8 weeks",
      format: "Intensive bootcamp with mentored project work"
    }
  ],
  
  // Implementation process
  implementationProcess: [
    {
      step: "1",
      title: "Assessment",
      description: "We evaluate your organization's current AI capabilities, skill gaps, and learning objectives to create a tailored training plan.",
      icon: "assessment"
    },
    {
      step: "2",
      title: "Customization",
      description: "Training materials and exercises are adapted to your industry, specific business processes, and team composition.",
      icon: "customization"
    },
    {
      step: "3",
      title: "Delivery",
      description: "Expert instructors deliver training through your preferred format: on-site, virtual, self-paced, or blended approaches.",
      icon: "delivery"
    },
    {
      step: "4",
      title: "Practical Application",
      description: "Participants work on real business challenges using AI tools and techniques relevant to their roles.",
      icon: "application"
    },
    {
      step: "5",
      title: "Evaluation",
      description: "Skills assessment and feedback sessions ensure learning objectives are met and identify areas for further development.",
      icon: "evaluation"
    },
    {
      step: "6",
      title: "Ongoing Support",
      description: "Post-training resources, office hours, and mentorship opportunities to reinforce learning and address questions.",
      icon: "support"
    }
  ],
  
  // Pricing tiers
  pricingTiers: [
    {
      name: "Essentials",
      description: "Perfect for small teams or organizations just beginning their AI journey",
      price: "$1,500",
      perUnit: "per person",
      features: [
        "AI Fundamentals Workshop",
        "Basic tool training",
        "Digital learning resources",
        "30 days email support",
        "Completion certificate"
      ],
      isPopular: false,
      ctaText: "Get Started"
    },
    {
      name: "Professional",
      description: "Comprehensive training for organizations serious about AI adoption",
      price: "$3,500",
      perUnit: "per person",
      features: [
        "All Essentials features",
        "Role-Based AI Skills Program",
        "Hands-on project work",
        "6 months expert support",
        "Implementation planning session",
        "AI readiness assessment"
      ],
      isPopular: true,
      ctaText: "Most Popular"
    },
    {
      name: "Enterprise",
      description: "Custom training solutions for large organizations with complex needs",
      price: "Custom",
      perUnit: "",
      features: [
        "All Professional features",
        "AI Implementation Certification",
        "Custom AI Solutions Development",
        "Executive briefing sessions",
        "On-site training options",
        "Dedicated training manager",
        "Unlimited support (12 months)"
      ],
      isPopular: false,
      ctaText: "Contact Sales"
    }
  ]
};

// AI Onboarding page data
export const aiOnboardingData = {
  // Add similar structure as above but for AI Onboarding
};

// Workflow Automation page data
export const workflowAutomationData = {
  // Add similar structure as above but for Workflow Automation
};

// Custom AI Development page data
export const customAIDevelopmentData = {
  // Add similar structure as above but for Custom AI Development
};

// AI System Optimization page data
export const aiOptimizationData = {
  // Add similar structure as above but for AI System Optimization
};
